import React from 'react';
import { AlertTriangle, Building2, ChevronDown, ExternalLink, MapPin, XCircle } from 'lucide-react';
import { describeDuplicate, type ValidatedCdmoImportRow } from '../../lib/cdmoImport/validateRow';
import { EVIDENCE_GROUP_LABELS } from './helpers';
import { EvidenceStatusBadge, LinkifiedText } from './shared';

const yesNoLabel = (value: boolean | null) => (value === null ? 'Not verified (saved as No)' : value ? 'Yes' : 'No');

const Section: React.FC<{ title: string; children: React.ReactNode; aside?: React.ReactNode }> = ({ title, children, aside }) => (
  <section className="rounded-xl border border-slate-gray/15 bg-white p-4">
    <div className="flex items-center justify-between gap-3 mb-3">
      <h5 className="text-xs font-semibold uppercase tracking-wide text-slate-gray">{title}</h5>
      {aside}
    </div>
    {children}
  </section>
);

const Chips: React.FC<{ values: string[] }> = ({ values }) => (
  <div className="flex flex-wrap gap-1.5">
    {values.map((value) => (
      <span key={value} className="rounded-md bg-light-gray border border-slate-gray/15 px-2 py-0.5 text-xs text-charcoal">
        {value}
      </span>
    ))}
  </div>
);

const Empty = () => <span className="text-slate-gray/70">—</span>;

const RowDetail: React.FC<{ row: ValidatedCdmoImportRow }> = ({ row }) => {
  const { profile, review } = row;
  const duplicateReason = describeDuplicate(row);

  const fields: Array<[string, React.ReactNode]> = [
    ['Business profile', profile.cdmo_business_profile ?? 'Mid-Size Specialist (default)'],
    ['Website', profile.website || <Empty />],
    ['Primary contact email', profile.email || <Empty />],
    ['Secondary emails', profile.secondary_emails.length ? profile.secondary_emails.join(', ') : <Empty />],
    ['Contact name', profile.contact_name || <Empty />],
    ['Phone', profile.phone || <Empty />],
    ['FEI numbers', profile.fei_numbers.length ? profile.fei_numbers.join(', ') : <Empty />],
    ['Parent company', profile.parent_company || <Empty />],
    ['Primary modalities', profile.primary_capabilities.length ? <Chips values={profile.primary_capabilities} /> : <Empty />],
    ['Typical batch sizes', profile.typical_batch_sizes.length ? <Chips values={profile.typical_batch_sizes} /> : <Empty />],
    ['Budget ranges', profile.budget_ranges.length ? <Chips values={profile.budget_ranges} /> : <Empty />],
    ['Target client types', profile.target_client_types.length ? <Chips values={profile.target_client_types} /> : <Empty />],
    ['Engagement scales', profile.engagement_scales.length ? <Chips values={profile.engagement_scales} /> : <Empty />],
    ['Project types', profile.preferred_project_types.length ? <Chips values={profile.preferred_project_types} /> : <Empty />],
    ['GMP certifications', profile.gmp_certifications.length ? <Chips values={profile.gmp_certifications} /> : <Empty />],
    ['Operating regions', profile.operating_regions.length ? <Chips values={profile.operating_regions} /> : <Empty />],
    ['Speed to start', profile.speed_to_start ?? <Empty />],
    ['Feasibility studies', yesNoLabel(profile.willingness_feasibility_studies)],
    ['Startup friendly', yesNoLabel(profile.startup_friendly)],
  ];

  const detailed = Object.entries(profile.detailed_capabilities);
  const facilitiesByRegion = profile.facilities.reduce<Record<string, typeof profile.facilities>>((groups, facility) => {
    const key = facility.region || 'Region not set';
    (groups[key] ||= []).push(facility);
    return groups;
  }, {});

  return (
    <div className="space-y-4" data-testid={`cdmo-import-detail-${row.rowNumber}`}>
      {(duplicateReason || row.errors.length > 0 || row.warnings.length > 0) && (
        <div className="space-y-3">
          {duplicateReason && (
            <div className="rounded-xl border border-slate-300 bg-slate-50 p-4 text-sm text-slate-700">
              <p className="font-semibold text-charcoal">{duplicateReason} — not imported.</p>
              {row.duplicate?.existingId && (
                <a
                  href={`/cdmo-application?edit=${row.duplicate.existingId}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mt-2 inline-flex items-center gap-1.5 font-semibold text-selected-blue hover:underline"
                >
                  Open existing profile <ExternalLink className="w-3.5 h-3.5" aria-hidden />
                </a>
              )}
            </div>
          )}
          {row.errors.length > 0 && (
            <div className="rounded-xl border border-red-200 bg-red-50 p-4">
              <p className="text-sm font-semibold text-red-800 mb-2">
                {row.errors.length} error{row.errors.length === 1 ? '' : 's'} — not imported
              </p>
              <ul className="space-y-1.5">
                {row.errors.map((message, index) => (
                  <li key={index} className="flex gap-2 text-sm text-red-700 break-words">
                    <XCircle className="w-4 h-4 shrink-0 mt-0.5" aria-hidden />
                    <span>{message}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
          {row.warnings.length > 0 && (
            <div className="rounded-xl border border-amber-200 bg-amber-50 p-4">
              <p className="text-sm font-semibold text-amber-900 mb-2">
                {row.warnings.length} warning{row.warnings.length === 1 ? '' : 's'}
              </p>
              <ul className="space-y-1.5">
                {row.warnings.map((message, index) => (
                  <li key={index} className="flex gap-2 text-sm text-amber-800 break-words">
                    <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" aria-hidden />
                    <span>{message}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}

      <div className="grid gap-4 lg:grid-cols-2">
        <Section title="Profile values">
          <dl className="divide-y divide-slate-gray/10">
            {fields.map(([label, value]) => (
              <div key={label} className="grid grid-cols-[9.5rem_1fr] gap-3 py-2 text-sm first:pt-0 last:pb-0">
                <dt className="text-slate-gray">{label}</dt>
                <dd className="text-charcoal min-w-0 break-words">{value}</dd>
              </div>
            ))}
          </dl>
        </Section>

        <div className="space-y-4">
          <Section title="Detailed capabilities">
            {detailed.length === 0 ? (
              <p className="text-sm text-slate-gray">None listed.</p>
            ) : (
              <div className="space-y-3">
                {detailed.map(([modality, capabilities]) => (
                  <div key={modality}>
                    <p className="text-sm font-medium text-charcoal mb-1.5">
                      {modality} <span className="text-slate-gray font-normal">({capabilities.length})</span>
                    </p>
                    <Chips values={capabilities} />
                  </div>
                ))}
              </div>
            )}
          </Section>

          <Section title="Description">
            {profile.description ? (
              <p className="text-sm text-charcoal whitespace-pre-wrap break-words">{profile.description}</p>
            ) : (
              <p className="text-sm text-slate-gray">None provided.</p>
            )}
          </Section>

          {profile.admin_notes && (
            <Section title="Admin notes from the file">
              <LinkifiedText text={profile.admin_notes} className="text-sm text-charcoal" />
            </Section>
          )}
        </div>
      </div>

      <Section
        title="Facilities"
        aside={<span className="text-xs text-slate-gray">{profile.facilities.length} listed</span>}
      >
        {profile.facilities.length === 0 ? (
          <p className="text-sm text-slate-gray">None listed.</p>
        ) : (
          <div className="space-y-4">
            {Object.entries(facilitiesByRegion).map(([region, facilities]) => (
              <div key={region}>
                <p className="text-sm font-semibold text-charcoal mb-2">{region}</p>
                <div className="grid gap-2 md:grid-cols-2">
                  {facilities.map((facility, index) => (
                    <div key={index} className="rounded-lg border border-slate-gray/15 bg-light-gray/50 p-3">
                      <p className="flex items-start gap-1.5 text-sm font-medium text-charcoal">
                        {facility.location ? (
                          <MapPin className="w-4 h-4 text-electric-blue shrink-0 mt-0.5" aria-hidden />
                        ) : (
                          <Building2 className="w-4 h-4 text-slate-gray shrink-0 mt-0.5" aria-hidden />
                        )}
                        <span className="break-words">{facility.location || `${region} — Facility ${index + 1}`}</span>
                      </p>
                      {facility.capabilities.length > 0 && (
                        <div className="mt-2">
                          <Chips values={facility.capabilities} />
                        </div>
                      )}
                      {facility.equipment && (
                        <p className="mt-2 text-xs text-slate-gray break-words">
                          <span className="font-medium text-charcoal">Equipment: </span>
                          {facility.equipment}
                        </p>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </Section>

      <Section
        title="Research review"
        aside={
          <span className="text-xs text-slate-gray">
            {review.research_date || 'No research date'} · {review.collected_via || 'No collection method'}
          </span>
        }
      >
        {review.review_summary ? (
          <LinkifiedText text={review.review_summary} className="text-sm text-charcoal" />
        ) : (
          <p className="text-sm text-slate-gray">No review summary.</p>
        )}
      </Section>

      <details className="group rounded-xl border border-slate-gray/15 bg-white">
        <summary className="flex cursor-pointer list-none items-center justify-between gap-3 p-4 [&::-webkit-details-marker]:hidden">
          <span className="text-xs font-semibold uppercase tracking-wide text-slate-gray">
            Evidence <span className="font-normal normal-case">({review.evidence.length} groups)</span>
          </span>
          <ChevronDown className="w-4 h-4 text-slate-gray transition-transform group-open:rotate-180" aria-hidden />
        </summary>
        <ul className="divide-y divide-slate-gray/10 px-4 pb-4">
          {review.evidence.map((item) => (
            <li key={item.group} className="py-3 first:pt-0 last:pb-0">
              <div className="flex flex-wrap items-center gap-2 mb-1">
                <span className="text-sm font-medium text-charcoal">{EVIDENCE_GROUP_LABELS[item.group]}</span>
                <EvidenceStatusBadge status={item.status} />
              </div>
              {item.evidence ? (
                <LinkifiedText text={item.evidence} className="text-xs text-slate-gray leading-relaxed" />
              ) : (
                <p className="text-xs text-slate-gray/70">No evidence given.</p>
              )}
            </li>
          ))}
        </ul>
      </details>
    </div>
  );
};

export default RowDetail;
